import { useCallback, useEffect, useRef, useState } from 'react';
import { useGetIdentity } from '@refinedev/core';
import type { IERPUser } from '@admin-panel/types';
import { isHubAlertRecipient } from '../hubAlerts/hubAlertRules';
import { isInstalledWindow } from '../hubAlerts/hubWindowMode';
import {
  REGISTRATION_GRACE_MS,
  detectDevice,
  pendingStep,
  setupApplies,
  type BrowserHints,
  type DeviceInfo,
  type HubAlertSetupMode,
  type SetupStep,
} from './hubAlertSetupRules';
import {
  clearTestPassed,
  currentSetupMode,
  getRegistrationStatus,
  previousRegistrationResult,
  readTestPassed,
  registrationPendingSince,
  requestEnableAlerts,
  subscribeSetupChanges,
} from './hubAlertSetupState';

export type HubAlertSetup = {
  mode: HubAlertSetupMode;
  /** The operator is not known yet (identity still loading). */
  loading: boolean;
  /** This operator and device are covered by setup (see `setupApplies`). */
  applies: boolean;
  /** First check finished and nothing is still registering. */
  settled: boolean;
  step: SetupStep | null;
  device: DeviceInfo;
  installed: boolean;
  permission: NotificationPermission | 'unsupported';
  /** Soft mode: show the red strip. */
  showStrip: boolean;
  /** Hard mode: show the setup page instead of Hub. */
  gate: boolean;
  /** Hard mode, operator or first check still unknown: show a loader rather than flashing Hub. */
  holding: boolean;
  refresh: () => void;
};

export const readDevice = () =>
  detectDevice(
    (navigator as Navigator & { userAgentData?: BrowserHints }).userAgentData ?? null,
    navigator.userAgent,
    navigator.maxTouchPoints ?? 0,
  );

const readInstalled = isInstalledWindow;

const readPermission = (): NotificationPermission | 'unsupported' =>
  'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window
    ? Notification.permission
    : 'unsupported';

/**
 * Whether this device is registered for order alerts; null while that is still being decided.
 *
 * HubAlerts' registration result decides: the browser subscription is created before Platform
 * stores the token, so a subscription alone does not prove Platform can reach this device. A
 * build without Firebase settings (`not-configured`) fails open: our own mistake never locks
 * operators out. Until a result exists (HubAlerts loads lazily and re-registers after every login,
 * because logout removes the subscription) an existing subscription counts, and otherwise the
 * grace period runs from `checkingSince`, so a shared phone never flashes "not set up".
 */
export const readPushReady = async (
  permission: NotificationPermission | 'unsupported',
  checkingSince: number,
): Promise<boolean | null> => {
  if (permission !== 'granted') return false;
  // While a retry runs, keep judging by the last result: the strip must not vanish for the length
  // of every attempt during an outage.
  const status = getRegistrationStatus() ?? previousRegistrationResult();
  if (status === 'on' || status === 'not-configured') return true;
  // A result from before permission was granted (for example unblocked in the phone's settings)
  // says nothing now; the hook re-registers and this waits for the new result.
  if (status !== null && !isStalePermissionStatus(status)) return false;
  try {
    const registration = await navigator.serviceWorker.getRegistration('/');
    if (await registration?.pushManager.getSubscription()) return true;
  } catch {
    // Treated as not subscribed below.
  }
  const since = registrationPendingSince() ?? checkingSince;
  return Date.now() - since < REGISTRATION_GRACE_MS ? null : false;
};

const isStalePermissionStatus = (status: string | null) =>
  status === 'needs-permission' || status === 'blocked';

/**
 * Register this device again now: permission is granted, but the last result predates it (allowed
 * outside Hub, in the phone or browser settings). Failures are retried by HubAlerts itself.
 */
const shouldReregister = (permission: NotificationPermission | 'unsupported') =>
  permission === 'granted' && isStalePermissionStatus(getRegistrationStatus());

type Snapshot = {
  device: DeviceInfo;
  installed: boolean;
  permission: NotificationPermission | 'unsupported';
  pushReady: boolean | null;
  testPassed: boolean;
};

/**
 * Live readiness of this device for order alerts. Re-checked on open, on return to the front,
 * when permission or install state changes, and (setup page, `poll`) every 2 s.
 */
export const useHubAlertSetup = ({ poll = false }: { poll?: boolean } = {}): HubAlertSetup => {
  const { data: user, isLoading: identityLoading } = useGetIdentity<IERPUser>();
  const [device] = useState(readDevice);
  const applies = setupApplies(
    isHubAlertRecipient(user?.roles),
    user?.adminUserAttributes?.warehouseIds?.length ?? 0,
    device.platform,
  );
  const [mode] = useState(currentSetupMode);
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const evaluating = useRef(0);
  const alive = useRef(true);
  const recheck = useRef<number | undefined>(undefined);
  // When checking started for this operator (a login on a shared phone starts a new grace period).
  const checkingSince = useRef(Date.now());

  const refresh = useCallback(() => {
    const run = ++evaluating.current;
    const permission = readPermission();
    if (shouldReregister(permission)) {
      // No prompt is needed: permission is already granted.
      requestEnableAlerts();
    }
    void readPushReady(permission, checkingSince.current).then(pushReady => {
      if (!alive.current || run !== evaluating.current) return;
      if (permission !== 'granted' && permission !== 'unsupported') clearTestPassed();
      setSnapshot(previous => ({
        device: readDevice(),
        installed: readInstalled(),
        permission,
        // A registration re-run (retry, re-bind) keeps the last answer instead of flashing a
        // loader over Hub; only the first check waits.
        pushReady: pushReady ?? previous?.pushReady ?? null,
        testPassed: readTestPassed(),
      }));
      window.clearTimeout(recheck.current);
      if (pushReady === null) {
        // Still registering: look again once the grace period is over.
        recheck.current = window.setTimeout(() => {
          if (alive.current && run === evaluating.current) refresh();
        }, REGISTRATION_GRACE_MS);
      }
    });
  }, []);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      window.clearTimeout(recheck.current);
    };
  }, []);

  useEffect(() => {
    if (!applies || mode === 'off') return undefined;
    let cancelled = false;
    checkingSince.current = Date.now();
    refresh();
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', refresh);
    const unsubscribe = subscribeSetupChanges(refresh);
    const installedQuery = window.matchMedia?.('(display-mode: standalone)');
    installedQuery?.addEventListener?.('change', refresh);
    let permissionStatus: PermissionStatus | null = null;
    void navigator.permissions
      ?.query({ name: 'notifications' as PermissionName })
      .then(status => {
        if (cancelled) return;
        permissionStatus = status;
        status.addEventListener('change', refresh);
      })
      .catch(() => undefined);
    const timer = poll ? window.setInterval(refresh, 2000) : undefined;
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', refresh);
      unsubscribe();
      installedQuery?.removeEventListener?.('change', refresh);
      permissionStatus?.removeEventListener('change', refresh);
      if (timer) window.clearInterval(timer);
    };
  }, [applies, mode, poll, refresh, user?.id]);

  const active = applies && mode !== 'off';
  const step = snapshot ? pendingStep(snapshot) : null;
  const settled = Boolean(snapshot) && snapshot?.pushReady !== null;
  const needsSetup = active && settled && step !== null;
  // A device that passed setup (installed, permission granted, sound confirmed) and now only fails
  // to reach the alert service (Platform or Firebase down, warehouse Wi-Fi) keeps working in hard
  // mode: the strip shows while registration is retried.
  const connectionBlip = step === 'connect' && Boolean(snapshot?.testPassed);
  return {
    mode,
    loading: identityLoading,
    applies,
    settled,
    step,
    device: snapshot?.device ?? device,
    installed: snapshot?.installed ?? false,
    permission: snapshot?.permission ?? 'default',
    showStrip: needsSetup && (mode === 'soft' || connectionBlip),
    gate: needsSetup && mode === 'hard' && !connectionBlip,
    // Hard mode never shows Hub before it knows who this is; an identity error fails open.
    holding: mode === 'hard' && (identityLoading || (active && !settled)),
    refresh,
  };
};
